import { Station, RadioBrowserStation, RadioBrowserCountry } from "../../types";

// List of available Radio Browser mirrors.
// The app will rotate through these if one fails.
const MIRRORS = [
  "https://de1.api.radio-browser.info/json",
  "https://at1.api.radio-browser.info/json",
  "https://nl1.api.radio-browser.info/json",
];

// Track the currently active mirror index to avoid checking dead mirrors repeatedly
let activeMirrorIndex = 0;

/**
 * Normalize station name for deduplication
 * "RIX FM", "Rix FM", "RIX FM 106.1" -> "rixfm"
 */
const normalizeStationName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/\s+/g, "") // Remove spaces
    .replace(/[0-9.]+\s*(fm|am|mhz|khz)?$/i, "") // Remove frequency suffix
    .replace(/[-_]/g, "") // Remove dashes/underscores
    .replace(/radio$/i, "") // Remove trailing "radio"
    .replace(/^radio/i, "") // Remove leading "radio"
    .trim();
};

/**
 * Check if a logo URL is valid/usable (not empty, not just a tiny favicon)
 */
const isValidLogoUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  if (url.length < 10) return false;
  // Google favicons are low quality fallbacks - prefer real logos
  if (url.includes("google.com/s2/favicons")) return false;
  // Empty or placeholder URLs
  if (url === "null" || url === "undefined") return false;
  return true;
};

/**
 * Smart deduplication: Groups stations by normalized name and picks the BEST one
 * Priority: Has real logo > Higher bitrate > More votes > Has tags
 */
const deduplicateStations = (stations: Station[]): Station[] => {
  const groups = new Map<string, Station[]>();

  // Group by normalized name
  for (const station of stations) {
    const key = normalizeStationName(station.name);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(station);
  }

  // For each group, pick the best station and merge metadata
  const result: Station[] = [];

  for (const [_key, group] of Array.from(groups.entries())) {
    if (group.length === 1) {
      result.push(group[0]);
      continue;
    }

    // Sort by quality score (higher is better)
    group.sort((a, b) => {
      // 1. Real logo is most important (worth 1000 points)
      const aHasLogo = isValidLogoUrl(a.logoUrl) ? 1000 : 0;
      const bHasLogo = isValidLogoUrl(b.logoUrl) ? 1000 : 0;

      // 2. Higher bitrate (worth up to 500 points)
      const aBitrate = Math.min(a.bitrate || 0, 320) * 1.5;
      const bBitrate = Math.min(b.bitrate || 0, 320) * 1.5;

      // 3. More votes (worth up to 200 points, logarithmic)
      const aVotes = Math.min(Math.log10((a.votes || 0) + 1) * 50, 200);
      const bVotes = Math.min(Math.log10((b.votes || 0) + 1) * 50, 200);

      // 4. Has tags (worth 50 points)
      const aTags = (a.tags?.length || 0) > 0 ? 50 : 0;
      const bTags = (b.tags?.length || 0) > 0 ? 50 : 0;

      // 5. Has state/location info (worth 30 points)
      const aState = a.state ? 30 : 0;
      const bState = b.state ? 30 : 0;

      const aScore = aHasLogo + aBitrate + aVotes + aTags + aState;
      const bScore = bHasLogo + bBitrate + bVotes + bTags + bState;

      return bScore - aScore; // Descending
    });

    // Take the best station as base
    const best = { ...group[0] };

    // Merge: steal good data from other duplicates
    for (let i = 1; i < group.length; i++) {
      const other = group[i];

      // Steal logo if we don't have one but they do
      if (!isValidLogoUrl(best.logoUrl) && isValidLogoUrl(other.logoUrl)) {
        best.logoUrl = other.logoUrl;
      }

      // Steal better bitrate
      if ((other.bitrate || 0) > (best.bitrate || 0)) {
        best.bitrate = other.bitrate;
        // If they have better bitrate, maybe better stream too
        if (other.streamUrl && other.streamUrl.includes("https")) {
          best.streamUrl = other.streamUrl;
        }
      }

      // Merge tags
      if (other.tags?.length) {
        const existingTags = new Set(best.tags || []);
        for (const tag of other.tags) {
          if (!existingTags.has(tag)) {
            best.tags = [...(best.tags || []), tag];
          }
        }
      }

      // Steal state/location if missing
      if (!best.state && other.state) {
        best.state = other.state;
      }

      // Steal website if missing
      if (!best.website && other.website) {
        best.website = other.website;
      }

      // Accumulate votes (take max)
      if ((other.votes || 0) > (best.votes || 0)) {
        best.votes = other.votes;
      }
    }

    result.push(best);
  }

  return result;
};

/**
 * robust fetch function that rotates mirrors on failure
 */
const fetchRb = async <T>(endpoint: string): Promise<T> => {
  // Try as many times as there are mirrors
  for (let i = 0; i < MIRRORS.length; i++) {
    // Use the current active mirror
    const baseUrl = MIRRORS[activeMirrorIndex];
    try {
      // Ensure endpoint starts with /
      const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
      const url = `${baseUrl}${path}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error(`RB HTTP ${response.status}`);
      return await response.json();
    } catch (e) {
      console.warn(`RadioBrowser mirror ${baseUrl} failed, rotating...`, e);
      // Rotate to next mirror for next attempt
      activeMirrorIndex = (activeMirrorIndex + 1) % MIRRORS.length;
    }
  }
  throw new Error("All RadioBrowser mirrors failed");
};

// Helper to clean up RadioBrowser tags
const cleanTags = (tagString: string): string[] => {
  if (!tagString) return [];
  return tagString
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
    .slice(0, 5);
};

const mapRbToStation = (rb: RadioBrowserStation): Station => {
  // Blacklist for known bad domains that cause console errors (cert issues, 404s)
  const BAD_DOMAINS = [
    "101smoothjazz.com",
    "radiovesti.ru",
    "placeholder",
    "default-logo",
    "no-image",
  ];

  // Enhanced Logo Logic with multiple fallback strategies
  let logoUrl = rb.favicon;

  // Check if initial logoUrl is valid
  if (logoUrl) {
    // Reject bad domains
    if (BAD_DOMAINS.some((d) => logoUrl.toLowerCase().includes(d))) {
      logoUrl = "";
    }
    // Reject tiny placeholder images (common pattern)
    if (logoUrl.includes("1x1") || logoUrl.includes("pixel")) {
      logoUrl = "";
    }
    // Reject Wikipedia/Wikimedia URLs that often fail
    if (
      logoUrl.includes("wikipedia.org") ||
      logoUrl.includes("wikimedia.org")
    ) {
      // Keep but mark as lower priority - will be replaced if duplicate has better
      // Actually, let's try to use it but with HTTPS
      logoUrl = logoUrl.replace("http://", "https://");
    }
  }

  // Upgrade HTTP to HTTPS for mixed content safety
  if (logoUrl && logoUrl.startsWith("http://")) {
    logoUrl = logoUrl.replace("http://", "https://");
  }

  // FALLBACK CHAIN if no logo:
  // We intentionally do NOT use Google Favicons here anymore
  // because they are too low quality (max 128px, often 16x16)
  // Instead, we leave logoUrl empty and let:
  // 1. deduplicateStations() find a duplicate with a real logo
  // 2. enrichStationData() in radioService match with static stations
  // 3. StationCard show a nice gradient placeholder

  let streamUrl = rb.url_resolved || rb.url;
  // Upgrade known HTTPS-capable CDNs to avoid mixed content warnings
  if (
    streamUrl &&
    streamUrl.includes("akamaized.net") &&
    streamUrl.startsWith("http://")
  ) {
    streamUrl = streamUrl.replace("http://", "https://");
  }

  return {
    id: rb.stationuuid,
    name: rb.name.trim(),
    country: rb.country,
    countryCode: rb.countrycode,
    state: rb.state || undefined,
    provider: "radio-browser",
    streamUrl: streamUrl,
    website: rb.homepage,
    logoUrl: logoUrl || "", // Keep empty if no good logo - will show nice placeholder
    bitrate: rb.bitrate || 0,
    codec: rb.codec,
    language: rb.language && typeof rb.language === 'string'
      ? rb.language.split(",").map((l: string) => l.trim())
      : Array.isArray(rb.language) ? rb.language : [],
    tags: cleanTags(rb.tags),
    votes: rb.clickcount,
    clickTrend: rb.clicktrend || 0,
    onlineStatus: rb.lastcheckok === 1 ? "online" : "offline",
  };
};

export const getRbStations = async (params: {
  countryCode?: string;
  country?: string;
  tag?: string;
  name?: string;
  limit?: number;
}): Promise<Station[]> => {
  const query = new URLSearchParams();

  query.append("hidebroken", "true");
  query.append("order", "clickcount");
  query.append("reverse", "true");

  const limit = params.limit || 30;
  // Fetch 3x more to have enough after deduplication and to find versions with logos
  query.append("limit", (limit * 3).toString());

  if (params.name) query.append("name", params.name);

  if (params.countryCode && params.countryCode !== "All") {
    query.append("countrycode", params.countryCode);
  } else if (params.country && params.country !== "All") {
    query.append("country", params.country);
  }

  if (params.tag) query.append("tag", params.tag);

  const hasFilter =
    params.name ||
    params.tag ||
    (params.countryCode && params.countryCode !== "All") ||
    (params.country && params.country !== "All");

  // Choose endpoint based on filter
  const endpoint = hasFilter ? "/stations/search" : "/stations/topclick";

  try {
    // Append query to endpoint
    const fullUrl = `${endpoint}?${query.toString()}`;
    const data = await fetchRb<RadioBrowserStation[]>(fullUrl);

    // Map all raw results to Station objects
    const allStations = data.map(mapRbToStation).filter((s: Station) => {
      if (!s.streamUrl) return false;
      if (
        s.bitrate > 0 &&
        s.bitrate < 64 &&
        !s.tags.includes("talk") &&
        !s.tags.includes("news")
      )
        return false;
      return true;
    });

    // CRITICAL: Deduplicate and pick best version (the one with logo!)
    const deduplicated = deduplicateStations(allStations);

    // Sort: stations with real logos first, then by votes
    deduplicated.sort((a, b) => {
      const aHasLogo = isValidLogoUrl(a.logoUrl) ? 1 : 0;
      const bHasLogo = isValidLogoUrl(b.logoUrl) ? 1 : 0;
      if (aHasLogo !== bHasLogo) return bHasLogo - aHasLogo;
      return (b.votes || 0) - (a.votes || 0);
    });

    return deduplicated.slice(0, limit);
  } catch (error) {
    console.error("RB Provider Error:", error);
    return [];
  }
};

export const getRbStationsByGeo = async (
  lat: number,
  long: number,
): Promise<Station[]> => {
  try {
    const query = new URLSearchParams({
      lat: lat.toString(),
      lon: long.toString(), // FIXED: Radio Browser uses 'lon' not 'long'
      distance: "200", // Increased from 100 to 200km for better coverage in Nordic countries
      hidebroken: "true",
      order: "distance", // Prioritize proximity over popularity
      limit: "100", // Fetch more to find duplicates with logos
    });

    const data = await fetchRb<RadioBrowserStation[]>(
      `/stations/search?${query.toString()}`,
    );
    const allStations = data.map(mapRbToStation);

    // Deduplicate and pick versions with logos
    const deduplicated = deduplicateStations(allStations);

    // Sort: stations with logos first, then by distance (votes as proxy)
    deduplicated.sort((a, b) => {
      const aHasLogo = isValidLogoUrl(a.logoUrl) ? 1 : 0;
      const bHasLogo = isValidLogoUrl(b.logoUrl) ? 1 : 0;
      if (aHasLogo !== bHasLogo) return bHasLogo - aHasLogo;
      return 0; // Keep original distance order
    });

    return deduplicated.slice(0, 50);
  } catch (e) {
    console.error("Geo search error", e);
    return [];
  }
};

export const getRbStationsForStats = async (
  country: string,
): Promise<Station[]> => {
  if (!country || country === "All") return [];

  const query = new URLSearchParams();
  query.append("country", country);
  query.append("hidebroken", "true");
  query.append("limit", "600"); // Fetch more to find duplicates with logos

  try {
    const data = await fetchRb<RadioBrowserStation[]>(
      `/stations/search?${query.toString()}`,
    );
    const allStations = data.map(mapRbToStation);

    // Deduplicate and pick versions with logos
    return deduplicateStations(allStations);
  } catch (e) {
    console.warn("RB Stats Error", e);
    return [];
  }
};

// New export to replace the manual fetch in radioService
export const getRbCountries = async (): Promise<RadioBrowserCountry[]> => {
  try {
    return await fetchRb<RadioBrowserCountry[]>("/countries");
  } catch (e) {
    console.error("Countries fetch error", e);
    return [];
  }
};

export const getRbStationById = async (id: string): Promise<Station | null> => {
  try {
    const data = await fetchRb<RadioBrowserStation[]>(`/stations/byuuid/${id}`);
    if (data && data.length > 0) {
      return mapRbToStation(data[0]);
    }
    return null;
  } catch (e) {
    console.error("RB Station by ID error", e);
    return null;
  }
};
